import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, 
  Phone, 
  MapPin, 
  Camera, 
  FileText, 
  CheckCircle,
  AlertCircle,
  Loader2,
  Send,
  Home,
  User,
  Settings,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/store/hooks';
import { Department, ComplaintCategory } from '@/types';

interface BotStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
}

const WhatsAppBotPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [categories, setCategories] = useState<ComplaintCategory[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ComplaintCategory | null>(null);
  const [complaintDetails, setComplaintDetails] = useState({
    summary: '',
    description: '',
    location: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent'
  });
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [complaintNumber, setComplaintNumber] = useState('');

  const steps: BotStep[] = [
    {
      id: 'phone',
      title: 'Verify Mobile Number',
      description: 'Enter your 10-digit mobile number for verification',
      icon: <Phone className="h-6 w-6" />,
      completed: false
    },
    {
      id: 'department',
      title: 'Select Department',
      description: 'Choose the department that should handle your complaint',
      icon: <Home className="h-6 w-6" />,
      completed: false
    },
    {
      id: 'category',
      title: 'Select Category',
      description: 'Choose the category that best describes your complaint',
      icon: <FileText className="h-6 w-6" />,
      completed: false
    },
    {
      id: 'details',
      title: 'Complaint Details',
      description: 'Provide detailed information about your complaint',
      icon: <User className="h-6 w-6" />,
      completed: false
    },
    {
      id: 'attachments',
      title: 'Attach Files',
      description: 'Upload any relevant photos or documents (optional)',
      icon: <Camera className="h-6 w-6" />,
      completed: false
    },
    {
      id: 'confirmation',
      title: 'Review & Submit',
      description: 'Review your complaint details and submit',
      icon: <CheckCircle className="h-6 w-6" />,
      completed: false
    }
  ];

  useEffect(() => {
    fetchDepartments();
    fetchCategories();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await fetch('/api/v1/departments', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/v1/complaint-categories', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const validateStep = (stepIndex: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (stepIndex) {
      case 0: // Phone verification
        if (!phoneNumber.match(/^\d{10}$/)) {
          newErrors.phoneNumber = 'Please enter a valid 10-digit mobile number';
        }
        break;
      case 1: // Department selection
        if (!selectedDepartment) {
          newErrors.department = 'Please select a department';
        }
        break;
      case 2: // Category selection
        if (!selectedCategory) {
          newErrors.category = 'Please select a category';
        }
        break;
      case 3: // Complaint details
        if (!complaintDetails.summary.trim()) {
          newErrors.summary = 'Please provide a summary of your complaint';
        }
        if (!complaintDetails.description.trim()) {
          newErrors.description = 'Please provide a detailed description';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
      setErrors({});
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('summary', complaintDetails.summary);
      formData.append('description', complaintDetails.description);
      formData.append('location', complaintDetails.location);
      formData.append('priority', complaintDetails.priority);
      formData.append('department_id', selectedDepartment?.id || '');
      formData.append('category_id', selectedCategory?.id || '');
      formData.append('phone_number', phoneNumber);
      
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch('/api/v1/complaints', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setComplaintNumber(data.data.complaint_number);
        setIsSubmitted(true);
      } else {
        const errorData = await response.json();
        setErrors({ general: errorData.message || 'Failed to submit complaint' });
      }
    } catch (error) {
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Phone verification
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Phone className="mx-auto h-16 w-16 text-blue-500 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Mobile Number</h3>
              <p className="text-gray-600">We need to verify your mobile number to process your complaint</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter 10-digit mobile number"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.phoneNumber ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
              )}
            </div>
          </div>
        );

      case 1: // Department selection
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Home className="mx-auto h-16 w-16 text-blue-500 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Select Department</h3>
              <p className="text-gray-600">Which department should handle your complaint?</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {departments.map((dept) => (
                <button
                  key={dept.id}
                  onClick={() => setSelectedDepartment(dept)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedDepartment?.id === dept.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-lg font-medium mr-4">
                      {dept.name.charAt(0)}
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium text-gray-900">{dept.name}</h4>
                      <p className="text-sm text-gray-600">{dept.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {errors.department && (
              <p className="text-sm text-red-600 text-center">{errors.department}</p>
            )}
          </div>
        );

      case 2: // Category selection
        return (
          <div className="space-y-6">
            <div className="text-center">
              <FileText className="mx-auto h-16 w-16 text-blue-500 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Select Category</h3>
              <p className="text-gray-600">Choose the category that best describes your complaint</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedCategory?.id === category.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center text-white text-lg font-medium mr-4">
                      {category.name.charAt(0)}
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium text-gray-900">{category.name}</h4>
                      <p className="text-sm text-gray-600">{category.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {errors.category && (
              <p className="text-sm text-red-600 text-center">{errors.category}</p>
            )}
          </div>
        );

      case 3: // Complaint details
        return (
          <div className="space-y-6">
            <div className="text-center">
              <User className="mx-auto h-16 w-16 text-blue-500 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Complaint Details</h3>
              <p className="text-gray-600">Provide detailed information about your complaint</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Summary *
                </label>
                <input
                  type="text"
                  value={complaintDetails.summary}
                  onChange={(e) => setComplaintDetails(prev => ({ ...prev, summary: e.target.value }))}
                  placeholder="Brief summary of your complaint"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.summary ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.summary && (
                  <p className="mt-1 text-sm text-red-600">{errors.summary}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={complaintDetails.description}
                  onChange={(e) => setComplaintDetails(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detailed description of the issue"
                  rows={4}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={complaintDetails.location}
                  onChange={(e) => setComplaintDetails(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Specific location (e.g., Block A, Floor 2, Apartment 201)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={complaintDetails.priority}
                  onChange={(e) => setComplaintDetails(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 4: // Attachments
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Camera className="mx-auto h-16 w-16 text-blue-500 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Attach Files</h3>
              <p className="text-gray-600">Upload any relevant photos or documents (optional)</p>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Camera className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">Click to upload files</p>
                <p className="text-sm text-gray-600">Images, PDFs, or documents</p>
              </label>
            </div>

            {attachments.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Attached Files:</h4>
                {attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-sm text-gray-900">{file.name}</span>
                    </div>
                    <button
                      onClick={() => removeAttachment(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 5: // Confirmation
        return (
          <div className="space-y-6">
            <div className="text-center">
              <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Review Your Complaint</h3>
              <p className="text-gray-600">Please review all details before submitting</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Mobile Number</label>
                  <p className="text-gray-900">{phoneNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Department</label>
                  <p className="text-gray-900">{selectedDepartment?.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Category</label>
                  <p className="text-gray-900">{selectedCategory?.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Priority</label>
                  <p className="text-gray-900 capitalize">{complaintDetails.priority}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Summary</label>
                <p className="text-gray-900">{complaintDetails.summary}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-gray-900">{complaintDetails.description}</p>
              </div>
              
              {complaintDetails.location && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Location</label>
                  <p className="text-gray-900">{complaintDetails.location}</p>
                </div>
              )}
              
              {attachments.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Attachments</label>
                  <p className="text-gray-900">{attachments.length} file(s) attached</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Complaint Submitted Successfully!</h2>
          <p className="text-gray-600 mb-4">
            Your complaint has been registered and assigned complaint number:
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-2xl font-bold text-blue-600">#{complaintNumber}</p>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            You will receive updates via WhatsApp and email. Our team will review your complaint and take appropriate action.
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                setIsSubmitted(false);
                setCurrentStep(0);
                setPhoneNumber('');
                setSelectedDepartment(null);
                setSelectedCategory(null);
                setComplaintDetails({ summary: '', description: '', location: '', priority: 'medium' });
                setAttachments([]);
                setComplaintNumber('');
              }}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Submit Another Complaint
            </button>
            <button
              onClick={() => navigate('/app/complaints')}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
            >
              View All Complaints
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/app/complaints')}
              className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Complaints
            </button>
          </div>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">WhatsApp Bot Complaint Registration</h1>
            <p className="text-gray-600">Quick and easy complaint registration using our WhatsApp bot interface</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  index <= currentStep
                    ? 'bg-blue-500 border-blue-500 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}>
                  {index < currentStep ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    index < currentStep ? 'bg-blue-500' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <h3 className="text-lg font-medium text-gray-900">{steps[currentStep].title}</h3>
            <p className="text-sm text-gray-600">{steps[currentStep].description}</p>
          </div>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white shadow rounded-lg p-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {currentStep === steps.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Complaint
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppBotPage;
